import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import SampleForm from "./components/sampleForm";
import { Box } from "@mui/material";
import { useSample } from "./utils/useSample";
import useAuth from "../../context/AuthContext";
import { SampleRegistrationTypes } from "@app/types/report.types";
import { UserType } from "@app/types/genericTables.types";
import { IFormNewSamples } from "@app/types/samples.types";
import { useUser } from "@services/servicesGenericsTables";
import { CreatePageList } from "@app/components/Layouts/PageList";
import { Loading, MButton } from "@app/components";
import MessageDialog from "@app/components/Login/MessageDialog";
import { yesNoTable } from "@app/utils/tables/shared.tables";
import { errorAlert } from "@app/utils/alerts";

const getDefaultValues = (
  sample?: SampleRegistrationTypes,
  user?: UserType,
  userList?: UserType[]
): IFormNewSamples => {
  const defaultAnalyst = userList?.find((u: UserType) => u.id === user?.id);
  const today = new Date();
  const year = today.getFullYear();

  return {
    id: sample?.id || undefined,
    analyst: (sample?.analyst as UserType) || defaultAnalyst,
    installation: sample?.installation || undefined,
    high_date: sample?.high_date ? sample.high_date : today,
    year: sample?.year || year,
    month: sample?.month,
    week: sample?.week || undefined,
    reason: sample?.reason || undefined,
    sampling_points: sample?.sampling_points || undefined,
    sample_type: sample?.sample_type || undefined,
    shared:
      sample?.shared !== undefined
        ? ((sample.shared ? yesNoTable[0] : yesNoTable[1]) as any)
        : undefined,
    client_reference: sample?.client_reference || undefined,
    masl_reference: sample?.masl_reference || undefined,
    accumulation: sample?.accumulation || undefined,
    sample_number: sample?.sample_number || undefined,
    analysis_type:
      sample?.analysis_type?.map((type: { id: number }) => type.id) || [],
  };
};

const EditSample = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [samplePreload, setSamplePreload] = useState<
    SampleRegistrationTypes | undefined
  >();
  const { save, get, loadingSave } = useSample();
  const { user } = useAuth();
  const { userList } = useUser();

  const form = useForm<IFormNewSamples>({
    mode: "all",
    defaultValues: getDefaultValues(samplePreload, user, userList),
  });

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [dataToSave, setDataToSave] = useState<IFormNewSamples | null>(null);

  useEffect(() => {
    if (id) {
      const sample = get(Number(id));
      if (!sample) {
        errorAlert("No se encontró la muestra.");
        navigate("/portal/muestras");
        return;
      }
      setSamplePreload(sample);
      const defaultValues = getDefaultValues(sample, user, userList);
      form.reset(defaultValues);
    }
  }, [id, user]);

  const handleSave = (data: IFormNewSamples) => {
    if (id) {
      setDataToSave(data);
      setOpenConfirmModal(true);
    } else {
      submit(data);
    }
  };

  const submit = async (data: IFormNewSamples) => {
    const status = await save(data, user as UserType);
    if (status) {
      navigate(-1);
    }
  };

  const handleBack = () => {
    navigate("/portal/muestras");
  };

  const handleDialogClose = () => setOpenConfirmModal(false);

  const handleDialogConfirm = () => {
    if (dataToSave) {
      submit(dataToSave);
    }
    handleDialogClose();
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(handleSave)}>
        <CreatePageList
          title={
            id
              ? `Editar muestra ${samplePreload?.masl_reference
                  .split(".")
                  .pop()}`
              : "Nueva muestra"
          }
          footer={
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                width: "100%",
              }}
            >
              <MButton
                variant="contained"
                color="primary"
                text="Guardar"
                type="submit"
              />
              <MButton
                variant="outlined"
                color="secondary"
                sx={{ ml: 2 }}
                text="Cancelar"
                onClick={handleBack}
              />
            </Box>
          }
        >
          <SampleForm />
        </CreatePageList>
      </form>

      <MessageDialog
        data={{
          component: "Confirm",
          title: "Guardar los cambios",
          message: "¿Está seguro de que desea guardar los cambios?",
          status: "confirm",
          active: openConfirmModal,
          handleConfirm: handleDialogConfirm,
        }}
        handleClose={handleDialogClose}
        handleConfirm={handleDialogConfirm}
      ></MessageDialog>

      {loadingSave && <Loading />}
    </FormProvider>
  );
};

export default EditSample;
